// src/pages/Annonces/AnnoncesPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { getAnnonces } from "./../../services/annoncesServices";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import Button from "./../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";
import CreateAnnonceModal from "./CreateAnnonceModal";
import AnnonceCard from "./AnnonceCard";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import { FaPlus } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const CATEGORIES = [
  "Recherche Enseignant",
  "Enseignant cherche club",
  "Recherche Matériel",
  "Don Matériel",
  "Autres",
];

const AnnoncesPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [allAnnonces, setAllAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const canCreate =
    userRole === "Administrateur" ||
    userRole === "Bureau" ||
    userRole === "Responsables_clubs";

  const fetchAnnonces = async () => {
    try {
      setLoading(true);
      const response = await getAnnonces();
      const sorted = (response || []).sort(
        (a, b) =>
          new Date(b.date_annonce).getTime() -
          new Date(a.date_annonce).getTime()
      );
      setAllAnnonces(sorted);
    } catch (error) {
      console.error("Failed to fetch annonces:", error);
      toast.error("Erreur lors du chargement des annonces.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnonces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAnnonces = useMemo(() => {
    const q = searchTitle.toLowerCase().trim();

    return allAnnonces.filter((a) => {
      if (!showAll && !a.is_active) return false;

      if (q && !a.title?.toLowerCase().includes(q)) return false;

      if (selectedCategory && a.category !== selectedCategory) return false;

      return true;
    });
  }, [allAnnonces, showAll, searchTitle, selectedCategory]);

  const handleAddAnnonce = () => setIsCreateModalOpen(true);
  const closeCreateModal = () => setIsCreateModalOpen(false);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] mt-[140px] dark:text-white">
        Chargement...
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      <div className="max-w-6xl mx-auto px-3 sm:px-4">
        <PageHeader
          eyebrow="Responsables Clubs"
          title="Petites annonces"
          align="center"
          size="lg"
        />

        <InfoBubble title="Annonces des clubs et enseignants">
          <p>
            Déposez et consultez les petites annonces des clubs girondins :
            recherche d’enseignant, matériel, dons, ou autres besoins. Seules
            les annonces actives sont affichées par défaut.
          </p>
        </InfoBubble>

        {/* Barre d’actions */}
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          {/* Bouton création */}
          {canCreate && (
            <Button
              text="Ajouter une annonce"
              icon={FaPlus}
              onClick={handleAddAnnonce}
              className="gap-2 self-start"
            />
          )}

          {/* Filtres */}
          <div className="w-full md:w-auto flex flex-col gap-2 md:flex-row md:items-center">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Rechercher par titre..."
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
                className="w-full text-sm px-3 py-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/70"
              />
            </div>

            <div className="flex-1 md:flex-none">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full md:w-52 text-sm px-3 py-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/70"
              >
                <option value="">Toutes les catégories</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Filtre Actives/Toutes */}
        <div className="mt-3 flex justify-end">
          <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-1 text-[11px]">
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${
                !showAll
                  ? "bg-primary text-white shadow-sm"
                  : "text-slate-700 dark:text-slate-200"
              }`}
            >
              Actives uniquement
            </button>
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${
                showAll
                  ? "bg-primary text-white shadow-sm"
                  : "text-slate-700 dark:text-slate-200"
              }`}
            >
              Toutes les annonces
            </button>
          </div>
        </div>

        {/* Liste d’annonces */}
        <div className="mt-5">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-40 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
                />
              ))}
            </div>
          ) : filteredAnnonces.length === 0 ? (
            <div className="mt-4 text-center text-sm bg-slate-50 dark:bg-slate-900/80 border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl px-4 py-6">
              Aucune annonce ne correspond à vos filtres.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {filteredAnnonces.map((annonce) => (
                <AnnonceCard
                  key={annonce.id}
                  annonce={annonce}
                  fetchAnnonces={fetchAnnonces}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateAnnonceModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        userId={user.userId}
        fetchAnnonces={fetchAnnonces}
      />
      <ToastContainer />
    </div>
  );
};

export default AnnoncesPage;
